import { useState, useCallback, useEffect } from 'react';

/**
 * Custom hook for managing Blob URLs with automatic cleanup.
 * 
 * @returns {Object} Hook state and controls
 * @returns {string|null} .url - The current blob URL
 * @returns {boolean} .loading - Whether a fetch is in progress
 * @returns {Error|null} .error - Any error from the last fetch operation
 * @returns {Function} .loadUrl - Function to load a new URL (accepts an async fetcher)
 * @returns {Function} .setUrl - Direct state setter for advanced use cases
 */
export function useBlobUrl() {
    const [url, setUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const loadUrl = useCallback(async (fetchFn) => {
        setLoading(true);
        setError(null);

        try {
            const blobUrl = await fetchFn();
            setUrl(prevUrl => {
                // Revoke old URL immediately before setting new one
                if (prevUrl?.startsWith('blob:')) {
                    URL.revokeObjectURL(prevUrl);
                }
                return blobUrl;
            });
            return blobUrl;
        } catch (err) {
            setError(err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    // Cleanup on URL change or unmount
    useEffect(() => {
        const urlToRevoke = url;
        return () => {
            if (urlToRevoke && urlToRevoke.startsWith('blob:')) {
                URL.revokeObjectURL(urlToRevoke);
            }
        };
    }, [url]);

    return { url, loading, error, loadUrl, setUrl };
}
